<script setup>
import { ref, inject, computed, watch } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import Stripe from '@/components/Stripe.vue'
import { useUserStore } from '@/stores/UserStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useRoute, useRouter } from 'vue-router'
import { BUY_ADDONS_STEPS, STRIPE_ERRORS } from '@/constants'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const selectedStepIdx = ref(0)
const haveSavedPayment = ref(true)
const scansBought = ref(false)
const selectedScanIdx = ref(0)
const stripeError = ref(null)
const paymentLoader = ref(false)

const addonsPaymentError = computed(() => billingStore.addonsPaymentError)
const buyMoreScansSuccess = computed(() => billingStore.buyMoreScansSuccess)
const buyMoreScansError = computed(() => billingStore.buyMoreScansError)
const stripe = computed(() => globalStore.stripe)

const selectScan = idx => {
  selectedStepIdx.value += 1
  selectedScanIdx.value = idx
  userStore.selectedPlanPrice = billingStore.getAddons[idx].cost
  userStore.fetchUserTaxInfo()
}

const buyScans = async () => {
  stripeError.value = ''
  paymentLoader.value = true

  if (!haveSavedPayment.value) {
    try {
      const { paymentMethod, error } = await stripe.value.createPaymentMethod({
        type: 'card',
        card: globalStore.card
      })

      if (error) {
        stripeError.value = STRIPE_ERRORS[error.code] || STRIPE_ERRORS[paymentError.value.error.decline_code] || error.message
        console.error(error, stripeError.value)
        throw Error(error)
        // Handle error
      } else {
        userStore.paymentMethod = paymentMethod

        await billingStore.createPaymentMethod({ 
          payment_method_id: paymentMethod.id
        }, 'addonsPaymentError')

        billingStore.buyMoreScans({ 
          stripe_payment_method_id: paymentMethod.id,
          add_on_id: billingStore.getAddons[selectedScanIdx.value].id,
          taxRate: userStore.tax?.taxRate,
        })
      }
    } catch (error) {
      console.error('buyScans', error)
      paymentLoader.value = false
    } 
  } else {
    try {
      billingStore.buyMoreScans({ 
        stripe_payment_method_id: userStore.user.stripe_default_payment_method?.id,
        add_on_id: billingStore.getAddons[selectedScanIdx.value].id, 
        taxRate: userStore.tax?.taxRate,
      })
    } catch (error) {
      console.error('buyScans if haveSavedPayment true', error)
      stripeError.value = STRIPE_ERRORS[error.code] || STRIPE_ERRORS[paymentError.value.error.decline_code] || error.message
      paymentLoader.value = false
    }
  }
}

const closeDialog = () => {
  router.replace(route.path)
  billingStore.buyScansDialog = {
    ...billingStore.buyScansDialog,
    open: false,
  }
  globalStore.openBuyScansModal = false
  haveSavedPayment.value = true
  setTimeout(() => {
    selectedStepIdx.value = 0
    scansBought.value = false
  }, 300)
}

const contactSales = () => {
  window.location.href = 'mailto:sales@carddealerpro.com'
}

watch(addonsPaymentError, () => {
  if (addonsPaymentError.value.message && !addonsPaymentError.value.ok) {
    paymentLoader.value = false
    stripeError.value = STRIPE_ERRORS[addonsPaymentError.value.error.code] || STRIPE_ERRORS[addonsPaymentError.value.error?.decline_code] || addonsPaymentError.value.message
  }
})

watch(() => billingStore.getAddons, () => {
  setTimeout(() => {
    if (billingStore.buyScansDialog.open && billingStore.getAddons && (route.query?.addonId || route.query?.addonId === '0')) {
      selectScan(Number(route.query?.addonId))
    }
  }, 100)
})

watch([buyMoreScansSuccess, buyMoreScansError], async () => {
  if (buyMoreScansError.value && !buyMoreScansError.value.ok) {
    stripeError.value = STRIPE_ERRORS[buyMoreScansError.value.error?.code] || STRIPE_ERRORS[buyMoreScansError.value.error?.decline_code] || buyMoreScansError.value.message
  }
  
  if (buyMoreScansSuccess.value.ok) {
    scansBought.value = true
    selectedScanIdx.value = selectedScanIdx.value
    configStore.fetchConfigs()
    userStore.fetchUser()
    billingStore.fetchInvoices()
  }

  paymentLoader.value = false
})

watch(() => configStore.config, () => {
  haveSavedPayment.value = Boolean(userStore.user?.stripe_default_payment_method)
});
</script>

<template>
  <section>
    <TransitionRoot as="template" :show="billingStore.buyScansDialog.open">
      <Dialog as="div" class="relative z-[9999]" @close="closeDialog">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
        </TransitionChild>
    
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex h-full w-full justify-center md:items-center md:p-[30px]">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              enter-to="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 md:scale-100"
              leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            >
              <DialogPanel
                class="
                  relative md:rounded-lg bg-white shadow-xl transition-all w-full md:max-w-2xl flex-1
                  max-md:overflow-hidden flex flex-col
                "
              >
                <section class="flex flex-col flex-1 min-h-0">
                  <header class="flex items-center justify-between border-b py-4 px-3 md:px-5">
                    <DialogTitle class="text-lg font-semibold">Buy More AI Credits</DialogTitle>
                    <button @click="closeDialog">
                      <Icon icon="heroicons:x-mark" class="text-xl" />  
                    </button>
                  </header>

                  <div
                    v-if="scansBought"
                    class="pt-10 pb-5 flex flex-col items-center text-center md:flex-1 px-3 md:px-5"
                  >
                    <div class="flex-1 flex flex-col items-center justify-center">
                      <img src="@/assets/3d-assets/icons/check.png" class="w-[6.5rem] mb-6" />
                      <p class="text-base font-medium mb-6 p-1">
                        <strong>{{ billingStore.getAddons[selectedScanIdx]?.quantity?.toLocaleString() }} AI Credits</strong> have been added to your account!
                      </p>
                      <button class="primary-button max-md:w-full px-9" @click="closeDialog">Done</button>
                    </div>
                  </div>

                  <template v-else>
                    <ul class="flex items-center justify-center gap-x-2 md:gap-x-4 py-4 px-3 md:px-5 text-gray-500">
                      <template v-for="step, idx in BUY_ADDONS_STEPS" :key="idx">
                        <li
                          class="flex items-center gap-x-1 md:gap-x-1.5"
                          :class="{ 'text-tint-1': selectedStepIdx === idx }"
                        >
                          <span
                            class="w-4 h-4 rounded-full bg-gray-500 text-white inline-flex items-center justify-center flex-shrink-0"
                            :class="{ '!bg-tint-1': selectedStepIdx === idx }"
                          >
                            <span v-if="selectedStepIdx <= idx" class="text-[10px]" v-text="idx + 1" />
                            <Icon icon="heroicons:check-20-solid" class="text-sm" v-else />
                          </span>
                          <span class="leading-tight text-xs md:text-sm font-medium" v-text="step" />
                        </li>
                        <Icon icon="heroicons:chevron-right-16-solid" class="text-xl" v-if="idx < BUY_ADDONS_STEPS.length - 1" />
                      </template>
                    </ul>

                    <ul class="grid grid-cols-2 md:grid-cols-3 gap-3 px-3 md:px-5 pb-5" v-if="selectedStepIdx === 0">
                      <li v-for="scan, idx in billingStore.getAddons" :key="idx" class="rounded-lg border p-3 relative overflow-hidden">
                        <div class="flex flex-col gap-y-2">
                          <h2 class="text-base text-gray-900 font-semibold" v-if="scan.title" v-text="scan.title"></h2>
                          <h3 class="flex items-baseline mb-2">
                            <strong class="text-xl font-bold leading-normal" v-text="`${scan.name.split(' ')[0]}`"></strong>
                            <span class="text-xs text-gray-500 ml-0.5">AI Credits</span>
                          </h3>
                        </div>
                        <h3 v-if="scan.cost" class="text-base mb-3" v-text="'$' + (scan.costLabel ? scan.costLabel : scan.cost)"></h3>
                        <h3 v-else class="text-base mb-3">Contact Sales</h3>

                        <button v-if="scan.cost" class="w-full py-1.5 text-xs primary-button" @click="selectScan(idx)">
                          Buy
                        </button>
                        <button v-else class="w-full py-1.5 text-xs alternative-button" @click="contactSales">
                          Contact Sales
                        </button>
                      </li>
                    </ul>

                    <section
                      v-else-if="selectedStepIdx === 1"
                      class="flex-1 min-h-0 max-md:overflow-y-auto px-3 md:px-5 pb-5"
                    >
                      <h4 class="text-xs font-semibold mb-2 text-gray-500">Buy more AI Credits</h4>

                      <div class="rounded-lg border p-3 relative overflow-hidden mb-5">
                        <div class="flex flex-col gap-y-2">
                          <h2 class="text-base text-gray-900 font-semibold" v-if="billingStore.getAddons[selectedScanIdx].title" v-text="billingStore.getAddons[selectedScanIdx].title"></h2>
                          <h3 class="flex items-baseline mb-2">
                            <strong class="text-xl font-bold leading-normal" v-text="`${billingStore.getAddons[selectedScanIdx].name.split(' ')[0]} ${!billingStore.getAddons[selectedScanIdx].cost ? '+' : ''}`"></strong>
                            <span class="text-xs text-gray-500 ml-0.5">AI Credits</span>
                          </h3>
                        </div>
                        <h3 class="text-base mb-3" v-text="'$' + (billingStore.getAddons[selectedScanIdx].costLabel ? billingStore.getAddons[selectedScanIdx].costLabel : billingStore.getAddons[selectedScanIdx].cost)"></h3>
                      </div>

                      <ul class="mb-6">
                        <li class="flex items-center justify-between text-xs mb-1.5">
                          <span class="text-gray-500">{{ billingStore.getAddons[selectedScanIdx].quantity?.toLocaleString() }} AI Credits</span>
                          <span>{{ '$' + (billingStore.getAddons[selectedScanIdx].costLabel ? billingStore.getAddons[selectedScanIdx].costLabel : billingStore.getAddons[selectedScanIdx].cost) }}</span>
                        </li>
                        <li class="flex items-center justify-between text-xs mb-1.5" v-if="userStore.user?.country === 'US'">
                          <span class="text-gray-500">Sales Tax</span>
                          <span>${{ userStore.tax?.taxAmount?.toFixed(2) }}</span>
                        </li>
                        <li class="flex items-center justify-between text-sm font-medium mb-1.5">
                          <span class="text-gray-900">Total</span>
                          <span>${{ userStore.tax?.finalAmount?.toFixed(2) }}</span>
                        </li>
                      </ul>

                      <section class="mb-6">
                        <header class="flex items-center justify-between text-xs font-semibold mb-2">
                          <h3>Payment Method</h3>
                          <button class="text-tint-1" v-if="haveSavedPayment" @click="haveSavedPayment = false">Change Payment Method</button>
                        </header>
                        <div>
                          <template v-if="haveSavedPayment">
                            <div class="flex items-center gap-x-2 h-10">
                              <Icon icon="heroicons:check-circle-solid" class="text-lg text-green-400" />
                              <Icon icon="heroicons:credit-card" class="text-lg" />
                              <span class="text-xs">•••• {{ userStore.user.stripe_default_payment_method?.last4 }}</span>
                            </div>
                            <div>
                              <span class="text-red-500">
                                {{ stripeError }}
                              </span>
                            </div>
                          </template>
                          <div v-else>
                            <Stripe :stripeError="stripeError" />
                          </div>
                        </div>
                      </section>

                      <footer>
                        <button 
                          class="primary-button max-md:w-full px-6" 
                          @click="buyScans"
                          :class="{'!p-0': paymentLoader}"
                          :disabled="paymentLoader"
                        > 
                          <div class="w-[106px]" v-if="paymentLoader">
                            <LoadingSpinner :width="'38px'" :height="'36px'" :background="'transparent'" />
                          </div>
                          <span v-else>Buy AI Credits</span>
                        </button>
                      </footer>
                    </section>
                  </template>
                </section>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </section>
</template>